import React, { createContext, useContext, useEffect, useState, ReactNode } from 'react';
import { User, Session, AuthError } from '@supabase/supabase-js';
import { supabase } from '../lib/supabase';
import { UserRole } from '../lib/roleUtils';
import { updateAccessToken } from '../lib/api';

interface AuthContextType {
  user: User | null;
  session: Session | null;
  loading: boolean;
  roles: UserRole[];
  activeRole: UserRole | null;
  waitlistStatus: string | null;
  playerState: string | null;
  setActiveRole: (role: UserRole) => Promise<void>;
  signUp: (email: string, password: string, fullName: string, signupRole: 'player' | 'owner', courseData?: { name: string; holes: number; city: string; state: string; country: string }) => Promise<{ error: AuthError | null }>;
  signIn: (email: string, password: string) => Promise<{ error: AuthError | null }>;
  signOut: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [session, setSession] = useState<Session | null>(null);
  const [loading, setLoading] = useState(true);
  const [roles, setRoles] = useState<UserRole[]>([]);
  const [activeRole, setActiveRoleState] = useState<UserRole | null>(null);
  const [waitlistStatus, setWaitlistStatus] = useState<string | null>(null);
  const [playerState, setPlayerState] = useState<string | null>(null);

  const loadUserSession = async (userId: string | null) => {
    if (!userId) {
      setRoles([]);
      setActiveRoleState(null);
      setWaitlistStatus(null);
      setPlayerState(null);
      return;
    }

    try {
      const rolesResponse = await supabase
        .from('user_roles')
        .select('role')
        .eq('user_id', userId);

      const profileResponse = await supabase
        .from('profiles')
        .select('active_role, waitlist_status, state')
        .eq('id', userId)
        .single();

      const userRoles = (rolesResponse.data ?? []).map((r: any) => r.role as UserRole);
      const userActiveRole = (profileResponse.data as any)?.active_role ?? null;

      setRoles(userRoles);
      setActiveRoleState(userActiveRole as UserRole | null);
      setWaitlistStatus((profileResponse.data as any)?.waitlist_status ?? null);
      setPlayerState((profileResponse.data as any)?.state ?? null);
    } catch (error) {
      console.error('Error loading user session:', error);
      setRoles([]);
      setActiveRoleState(null);
      setWaitlistStatus(null);
      setPlayerState(null);
    }
  };

  useEffect(() => {
    const initAuth = async () => {
      // Process cross-origin session tokens passed as query params from discgo.golf.
      // We use query params (not hash) so Supabase's detectSessionInUrl doesn't
      // intercept them — which would consume the hash before our code runs.
      const params = new URLSearchParams(window.location.search);
      const accessToken = params.get('sb_access_token');
      const refreshToken = params.get('sb_refresh_token');
      if (accessToken && refreshToken) {
        await supabase.auth.setSession({ access_token: accessToken, refresh_token: refreshToken });
        window.history.replaceState({}, document.title, window.location.pathname);
      }

      const { data: { session } } = await supabase.auth.getSession();
      updateAccessToken(session?.access_token ?? null);
      setSession(session);
      setUser(session?.user ?? null);
      await loadUserSession(session?.user?.id ?? null);
      setLoading(false);
    };

    initAuth();

    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange((_event, session) => {
      updateAccessToken(session?.access_token ?? null);
      setSession(session);
      setUser(session?.user ?? null);
      if (session?.user?.id) {
        loadUserSession(session.user.id);
      } else {
        setRoles([]);
        setActiveRoleState(null);
      }
    });

    return () => subscription.unsubscribe();
  }, []);

  const DISCGO_SITE_URL = import.meta.env.VITE_DISCGO_SITE_URL || 'http://localhost:3000';

  const signUp = async (email: string, password: string, fullName: string, signupRole: 'player' | 'owner', courseData?: { name: string; holes: number; city: string; state: string; country: string }) => {
    const nameParts = fullName.trim().split(' ');
    const firstName = nameParts[0] || '';
    const lastName = nameParts.slice(1).join(' ') || '';

    const metadata: Record<string, string | number> = {
      first_name: firstName,
      last_name: lastName,
      signup_role: signupRole,
    };

    if (signupRole === 'owner' && courseData) {
      metadata.course_name = courseData.name;
      metadata.course_holes = courseData.holes;
      metadata.course_city = courseData.city;
      metadata.course_state = courseData.state;
      metadata.course_country = courseData.country;
    }

    const { data, error } = await supabase.auth.signUp({
      email,
      password,
      options: {
        emailRedirectTo: `${DISCGO_SITE_URL}/login`,
        data: metadata,
      },
    });

    if (!error && data.user) {
      try {
        const { error: profileError } = await supabase
          .from('profiles')
          .upsert({
            id: data.user.id,
            first_name: firstName,
            last_name: lastName,
          }, {
            onConflict: 'id'
          });

        if (profileError) {
          console.error('Error creating user profile:', profileError);
        }

        const { error: roleError } = await supabase
          .from('user_roles')
          .insert({ user_id: data.user.id, role: signupRole });

        if (roleError) {
          console.error('Error creating user role:', roleError);
        }
      } catch (err) {
        console.error('Failed to create user profile/role:', err);
      }
    }

    return { error };
  };

  const signIn = async (email: string, password: string) => {
    const { error } = await supabase.auth.signInWithPassword({
      email,
      password,
    });
    return { error };
  };

  const signOut = async () => {
    sessionStorage.setItem('explicit-signout', '1');
    await supabase.auth.signOut({ scope: 'local' });
    Object.keys(localStorage).forEach(key => {
      if (key.startsWith('sb-')) localStorage.removeItem(key);
    });
  };

  const setActiveRole = async (role: UserRole) => {
    if (!user?.id) return;

    if (!roles.includes(role)) {
      console.error(`Cannot set active role to ${role}: user does not possess this role`);
      return;
    }

    const { error } = await supabase
      .from('profiles')
      .update({ active_role: role } as any)
      .eq('id', user.id);

    if (error) {
      console.error('Error updating active role:', error);
      return;
    }

    setActiveRoleState(role);
  };

  const value = {
    user,
    session,
    loading,
    roles,
    activeRole,
    waitlistStatus,
    playerState,
    setActiveRole,
    signUp,
    signIn,
    signOut,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}
